import _DebugPane from '../_app/cuchillo/3D/debug-utils/DebugPane';
import CharacterAnimationController from './CharacterAnimationController';
import { DEPTH_SETTINGS } from './DepthImage';
import TransitionController from './TransitionController';
import Scene, { LIGHT_SETTINGS, PRODUCT_LIGHTS_LUMINARIS, CHAR_ROT_SETTINGS, GENERAL_SETTINGS } from './Scene';
import { BG_SETTINGS } from './BGSphere';
import { MOUSE_SETTINGS } from '../_app/cuchillo/3D/WebGLObject';
import { SPIN_SETTINGS, FRONT_PLANE_SETTINGS } from './Box';
import BG from '../_app/cuchillo/layout/Background';
import { Metrics } from '../_app/cuchillo/core/Metrics';

export default class DebugPane extends _DebugPane {
  static _depthFolder = null;
  static _transitionFolder = null;
  static _bgFolder = null;
  static _repulsionFolder = null;
  static _mouseFolder = null;
  static _spinFolder = null;
  static _phongFolder = null;
  static _paletteFolder = null;
  static _circleFolder = null;
  static _lightsFolder = null;
  static _characterFolder = null;

  static async init() {
    await super.init();
  }

  // --- Background ---
  static addBackground() {
    if (!this.pane) return;
    this._bgFolder = this.pane.addFolder({ title: 'Background', expanded: false });

    this._bgFolder.addBinding(BG_SETTINGS, 'kill', { label: 'Kill', step: 0.01, min: 0, max: 1 });
    this._bgFolder.addBinding(BG_SETTINGS, 'seed', { label: 'Seed', step: 0.01, min: 0.01, max: 100 });
    this._bgFolder.addBinding(BG_SETTINGS, 'count', { label: 'Count', step: 1, min: 1, max: 32 });
    this._bgFolder.addBinding(BG_SETTINGS, 'blur', { label: 'Blur', step: 0.01, min: 0, max: 0.3 });
    this._bgFolder.addBinding(BG_SETTINGS, 'globalIntensity', { label: 'Intensity', step: 0.01, min: 0, max: 1 });
    this._bgFolder.addBinding(BG_SETTINGS, 'rotationY', { label: 'Rotation Y', step: 0.01, min: -2, max: 2 });
    this._bgFolder.addButton({
      title: 'Reseed'
    }).on('click', () => {
      Scene.reseed(() => { if (this.pane) this.pane.refresh(); });
    });

    const widthFolder = this._bgFolder.addFolder({ title: 'Width', expanded: false });
    widthFolder.addBinding(BG_SETTINGS, 'widthMin', { label: 'Min', step: 0.001, min: 0.001, max: 0.5 });
    widthFolder.addBinding(BG_SETTINGS, 'widthMax', { label: 'Max', step: 0.001, min: 0.001, max: 0.5 });

    const heightFolder = this._bgFolder.addFolder({ title: 'Height', expanded: false });
    heightFolder.addBinding(BG_SETTINGS, 'heightMin', { label: 'Min', step: 0.001, min: 0.001, max: 0.8 });
    heightFolder.addBinding(BG_SETTINGS, 'heightMax', { label: 'Max', step: 0.001, min: 0.001, max: 1.0 });

    const intensityFolder = this._bgFolder.addFolder({ title: 'Rect intensity', expanded: false });
    intensityFolder.addBinding(BG_SETTINGS, 'intensityMin', { label: 'Min', step: 0.01, min: 0, max: 1 });
    intensityFolder.addBinding(BG_SETTINGS, 'intensityMax', { label: 'Max', step: 0.01, min: 0, max: 1 });

    const animFolder = this._bgFolder.addFolder({ title: 'Animation', expanded: false });
    animFolder.addBinding(BG_SETTINGS, 'timeScale', { label: 'Anim speed', step: 0.01, min: 0, max: 2 });
    animFolder.addBinding(BG_SETTINGS, 'lifeScale', { label: 'Life speed', step: 0.01, min: 0, max: 2 });
    animFolder.addBinding(BG_SETTINGS, 'dispMin', { label: 'Disp min', step: 0.01, min: 0, max: 0.3 });
    animFolder.addBinding(BG_SETTINGS, 'dispMax', { label: 'Disp max', step: 0.01, min: 0.01, max: 0.5 });
    animFolder.addBinding(BG_SETTINGS, 'centerWidth', { label: 'White zone %', step: 0.01, min: 0, max: 0.95 });
    animFolder.addBinding(BG_SETTINGS, 'lifeMin', { label: 'Life min', step: 0.1, min: 0.5, max: 20 });
    animFolder.addBinding(BG_SETTINGS, 'lifeMax', { label: 'Life max', step: 0.1, min: 0.5, max: 30 });

    // const mouseFolder = this._bgFolder.addFolder({ title: 'Mouse', expanded: true });
    // mouseFolder.addBinding(BG_SETTINGS, 'mouseX', { label: 'Rot X', step: 0.01, min: 0, max: 2 });
    // mouseFolder.addBinding(BG_SETTINGS, 'mouseX', { label: 'Rot Y', step: 0.01, min: 0, max: 2 });
    // mouseFolder.addBinding(BG_SETTINGS, 'lerpY', { label: 'Factor X', step: 0.001, min: 0, max: 1 });
    // mouseFolder.addBinding(BG_SETTINGS, 'lerpY', { label: 'Factor Y', step: 0.001, min: 0, max: 1 });
    // mouseFolder.addBinding(BG_SETTINGS, 'displaceZ', { label: 'Dist Z', step: 1, min: 0, max: 1000 });
  }

  static removeBackground() {
    if (this._bgFolder) { this._bgFolder.dispose(); this._bgFolder = null; }
  }

  // --- Spin (drag rotation) & Box material ---
  // box: instància de Box. El color base i la shininess s'apliquen a TOTS els seus meshes
  // (Box clona el material per mesh, així que cal recórrer-los, no el material original passat).
  static addBox(box, settings) {
    if (!this.pane) return;

    // Aplica fn a CADA material de cada mesh del box (un mesh pot tenir array de materials;
    // si no es recorre l'array, ni color ni shininess s'apliquen). S'exclou el frontPlane (label):
    // té material propi i no ha de rebre el color/shininess del box.
    const eachMaterial = (fn) => box.traverse(o => {
      if (!o.isMesh || !o.material || o === box.frontPlane) return;
      (Array.isArray(o.material) ? o.material : [o.material]).forEach(fn);
    });

    this._spinFolder = this.pane.addFolder({ title: 'Box', expanded: true });
    this._spinFolder.addBinding(SPIN_SETTINGS, 'intensity', { label: 'Intensity', step: 0.0001, min: 0, max: 0.05 });
    this._spinFolder.addBinding(SPIN_SETTINGS, 'friction', { label: 'Friction', step: 0.001, min: 0.5, max: 0.999 });
    this._spinFolder.addBinding(settings, 'color', { view: 'color', label: 'Color' })
      .on('change', ({ value }) => eachMaterial(m => { if (m.color) m.color.set(value); }));
    this._spinFolder.addBinding(settings, 'shininess', { label: 'Shininess', step: 1, min: 0, max: 300 })
      .on('change', ({ value }) => eachMaterial(m => { if ('shininess' in m) { m.shininess = value; m.needsUpdate = true; } }));

    // Mida del plane frontal (label): multiplicador uniforme per ajustar-lo a ull al box.
    this._spinFolder.addBinding(FRONT_PLANE_SETTINGS, 'scale', { label: 'Label scale', step: 0.01, min: 0.1, max: 3 })
      .on('change', ({ value }) => { if (box.frontPlane) box.frontPlane.scale.setScalar(value); });
  }

  static removeBox() {
    if (this._spinFolder) { this._spinFolder.dispose(); this._spinFolder = null; }
  }

  // --- Lights ---
  static addLights() {
    if (!this.pane) return;
    this._lightsFolder = this.pane.addFolder({ title: 'Lights', expanded: true });

    const ambFolder = this._lightsFolder.addFolder({ title: 'Ambient', expanded: true });
    ambFolder.addBinding(LIGHT_SETTINGS.ambient, 'intensity', { label: 'Intensity', step: 0.01, min: 0, max: 5 })
      .on('change', ({ value }) => { if (Scene._ambientLight) Scene._ambientLight.intensity = value; });

    const dir1Folder = this._lightsFolder.addFolder({ title: 'Dir 1 (+X+Z)', expanded: true });
    dir1Folder.addBinding(PRODUCT_LIGHTS_LUMINARIS.dir1, 'color', { view: 'color', label: 'Color' })
      .on('change', ({ value }) => { if (Scene._prodDir1) Scene._prodDir1.color.set(value); });
    dir1Folder.addBinding(PRODUCT_LIGHTS_LUMINARIS.dir1, 'intensity', { label: 'Intensity', step: 0.1, min: 0, max: 20 })
      .on('change', ({ value }) => { if (Scene._prodDir1) Scene._prodDir1.intensity = value; });

    const dir2Folder = this._lightsFolder.addFolder({ title: 'Dir 2 (-X-Z)', expanded: true });
    dir2Folder.addBinding(PRODUCT_LIGHTS_LUMINARIS.dir2, 'color', { view: 'color', label: 'Color' })
      .on('change', ({ value }) => { if (Scene._prodDir2) Scene._prodDir2.color.set(value); });
    dir2Folder.addBinding(PRODUCT_LIGHTS_LUMINARIS.dir2, 'intensity', { label: 'Intensity', step: 0.1, min: 0, max: 20 })
      .on('change', ({ value }) => { if (Scene._prodDir2) Scene._prodDir2.intensity = value; });

    const pointFolder = this._lightsFolder.addFolder({ title: 'Point (-X+Z)', expanded: true });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'color', { view: 'color', label: 'Color' })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.color.set(value); });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'intensity', { label: 'Intensity', step: 0.001, min: 0, max: 2 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.intensity = value * Metrics.WIDTH; });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'distance', { label: 'Distance', step: 10, min: 0, max: 5000 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.distance = value; });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'decay', { label: 'Decay', step: 0.01, min: 0, max: 5 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.decay = value; });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'posX', { label: 'Pos X', step: 0.01, min: -2, max: 2 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.position.x = value * Metrics.WIDTH; });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'posY', { label: 'Pos Y', step: 0.01, min: 0, max: 2 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.position.y = value * Metrics.HEIGHT; });
    pointFolder.addBinding(PRODUCT_LIGHTS_LUMINARIS.point, 'posZ', { label: 'Pos Z', step: 0.01, min: -2, max: 2 })
      .on('change', ({ value }) => { if (Scene._prodPoint) Scene._prodPoint.position.z = value * Metrics.WIDTH; });
  }

  static removeLights() {
    if (this._lightsFolder) { this._lightsFolder.dispose(); this._lightsFolder = null; }
  }

  // --- Circle (products scroll carousel) ---
  static addCircle(settings, bottleSettings) {
    if (!this.pane) return;
    this._circleFolder = this.pane.addFolder({ title: 'Circle', expanded: false });
    this._circleFolder.addBinding(settings, 'radiusFactor', { label: 'Radius factor', step: 0.001, min: 0.05, max: 1 });
    this._circleFolder.addBinding(settings, 'tilt', { label: 'Tilt (X)', step: 0.01, min: -Math.PI * 2, max: Math.PI * 2 });
    this._circleFolder.addBinding(settings, 'scrollSpeed', { label: 'Group Z speed', step: 0.0001, min: 0, max: 0.02 });
    this._circleFolder.addBinding(settings, 'zScale', { label: 'Z size', step: 0.0001, min: -0.01, max: 0.01 });
    this._circleFolder.addBinding(settings, 'velZSpeed', { label: 'Vel Z tilt', step: 0.0001, min: -0.01, max: 0.01 });

    // Rotació del Character (separada dels thumbnails). Signe = sentit (negatiu = contrari)
    this._circleFolder.addBinding(CHAR_ROT_SETTINGS, 'speed', { label: 'Char rot speed', step: 0.0001, min: -0.02, max: 0.02 });

    const bokehFolder = this._circleFolder.addFolder({ title: 'Bokeh', expanded: false });
    bokehFolder.addBinding(settings, 'bokehMaxBlur', { label: 'Max blur', step: 0.001, min: 0, max: 0.05 });
    bokehFolder.addBinding(settings, 'bokehBlurPower', { label: 'Blur power', step: 0.000001, min: 0, max: 0.001 });

    if (bottleSettings) {
      const bottleFolder = this._circleFolder.addFolder({ title: 'Bottle', expanded: true });
      bottleFolder.addBinding(bottleSettings, 'rotSpeedY', { label: 'Rot speed Y', step: 0.0001, min: 0, max: 0.02 });
    }
  }

  static removeCircle() {
    if (this._circleFolder) { this._circleFolder.dispose(); this._circleFolder = null; }
  }

  // --- Character animations & morph targets (només a la Home) ---
  static addCharacter() {
    if (!this.pane || this._characterFolder) return;
    this._characterFolder = this.pane.addFolder({ title: 'Character', expanded: false });

    // Botó reset del helmet (frame 0 + opacity 0 + offset de repòs)
    this._characterFolder.addButton({ title: 'resetHelmet()' })
      .on('click', () => { CharacterAnimationController.resetHelmet(); Scene.resetTaponLights(); });

    // General — cinemàtica del character en 3 passos (translació pura). Tot tunejable.
    const genFolder = this._characterFolder.addFolder({ title: 'General (cinemàtica 3 passos)', expanded: true });

    // Estat inicial del helmet (offset Y respecte el character, × alçada del char)
    genFolder.addBinding(GENERAL_SETTINGS, 'helmetRestYFactor', { label: 'Helmet rest Y (× charH)', step: 0.01, min: -3, max: 3 });

    // ── Pas 1 ──
    const st1 = genFolder.addFolder({ title: 'Pas 1 — show helmet + apropa', expanded: true });
    st1.addBinding(GENERAL_SETTINGS, 'helmetShowDuration', { label: 'Helmet up dur (s)', step: 0.1, min: 0.1, max: 5 });
    st1.addBinding(GENERAL_SETTINGS, 'helmetToYFactor', { label: 'Helmet to Y (× charH)', step: 0.01, min: -3, max: 3 });
    st1.addBinding(GENERAL_SETTINGS, 'step1Duration', { label: 'Grup dur (s)', step: 0.1, min: 0.1, max: 5 });
    st1.addBinding(GENERAL_SETTINGS, 'step1GroupYFactor', { label: 'Grup Y desktop (× charH)', step: 0.01, min: -3, max: 3 });
    st1.addBinding(GENERAL_SETTINGS, 'step1GroupYFactorTablet', { label: 'Grup Y tablet (× charH)', step: 0.01, min: -3, max: 3 });
    st1.addBinding(GENERAL_SETTINGS, 'step1GroupYFactorMobile', { label: 'Grup Y mobile (× charH)', step: 0.01, min: -3, max: 3 });
    st1.addBinding(GENERAL_SETTINGS, 'step1CharZ', { label: 'Char Z apropa', step: 10, min: -2000, max: 3000 });
    st1.addBinding(GENERAL_SETTINGS, 'taponAnimDelay', { label: 'Anim tapon delay (s)', step: 0.1, min: 0, max: 10 });
    st1.addButton({ title: '▶ Run pas 1' }).on('click', () => Scene.generalStep1());
    st1.addButton({ title: '↺ Reset' }).on('click', () => Scene.resetGeneral());

    // ── Pas 2 ──
    const st2 = genFolder.addFolder({ title: 'Pas 2 — torna a inici', expanded: true });
    st2.addBinding(GENERAL_SETTINGS, 'step2Delay', { label: 'Delay (s, des de pas 1)', step: 0.1, min: 0, max: 15 });
    st2.addBinding(GENERAL_SETTINGS, 'step2YDuration', { label: 'Y dur (s, ease out)', step: 0.1, min: 0.1, max: 8 });
    st2.addBinding(GENERAL_SETTINGS, 'step2ZDuration', { label: 'Z dur (s, ease out)', step: 0.1, min: 0.1, max: 8 });
    st2.addBinding(GENERAL_SETTINGS, 'step2ZStartFactor', { label: 'Z start (×anim dur)', step: 0.01, min: 0, max: 1 });
    st2.addButton({ title: '▶ Run pas 2' }).on('click', () => Scene.generalStep2());
    st2.addButton({ title: '↺ Reset (→ final pas 1)' }).on('click', () => Scene.resetGeneralToStep1End());

    // ── Pas 3 ──
    const st3 = genFolder.addFolder({ title: 'Pas 3 — fi (navega)', expanded: true });
    st3.addButton({ title: '▶ Run pas 3' }).on('click', () => Scene.generalStep3());
    st3.addButton({ title: '↺ Reset (→ final pas 2)' }).on('click', () => Scene.resetGeneralToStep2End());

    // Seqüència completa (1→2→3)
    genFolder.addButton({ title: '▶▶ Full (animateGeneral)' }).on('click', () => Scene.animateGeneral());
  }

  static removeCharacter() {
    if (this._characterFolder) { this._characterFolder.dispose(); this._characterFolder = null; }
  }

}
